import React, { useCallback, useState } from "react";
import { Col, Form, Modal, Row, Space, Spin } from "antd";
import Title from "antd/lib/typography/Title";
import { Option } from "antd/lib/mentions";
import { useSelector } from "react-redux";
import CustomButton from "components/Common/buttons/CustomButton";
import { DictionariesSelector } from "store/dictionaries/selectors";
import { convertBulkCards } from "utils/api";
import { defaultNgiErrorMethod } from "components/Common/responses/message";
import NGIInput from "components/Common/inputs/NGIInput";
import NGISelect from "components/Common/inputs/NGISelect";
import NGIForm from "components/Common/form/NGIForm";
import NGIFormItem from "components/Common/form/NGIFormItem";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { makeStyles } from "@material-ui/core/styles";
import { getTrimmedObjectValues } from "utils/helpers/functions";
import { USER_TENANT_NAME } from "utils/constants";

const useStyles = makeStyles({
  titleWithDescription: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  modalContent: {
    padding: "30px 0 10px 0",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    borderLeft: "none",
    borderRight: "none",
    marginBottom: 24,
  },
  statusContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "10px 0 30px 0",
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    marginBottom: 24,
  },
  successContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    width: "100%",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  row: {
    justifyContent: "space-between",
  },
});

const CardConversionModal = ({ onCancel }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const [isLoading, setLoader] = useState(false);
  const [resultId, setResultId] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);

  const openConfirmationModal = useCallback(() => setIsConfirmationOpen(true), []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const openCancelConfirmationModal = useCallback(() => setIsCancelConfirmationOpen(true), []);
  const closeCancelConfirmationModal = useCallback(() => setIsCancelConfirmationOpen(false), []);

  const onConfirmClosingDialog = useCallback(() => {
    setResultId(null);
    closeCancelConfirmationModal();
    onCancel();
  }, []);

  const {
    list: { depot },
  } = useSelector(DictionariesSelector);

  const handleSave = useCallback(() => {
    const values = getTrimmedObjectValues(form.getFieldsValue());
    setLoader(true);
    closeConfirmationModal();

    convertBulkCards({ ...values, count: parseInt(values.count) })
      .then(res => {
        setLoader(false);
        const {
          data: { result },
        } = res;
        setResultId(result.id);
        openCancelConfirmationModal();
      })
      .catch(error => {
        setLoader(false);
        defaultNgiErrorMethod(error);
      });
  }, []);

  const getCount = useCallback(() => form.getFieldValue("count"), [form]);

  return (
    <Modal
      open={true}
      title={
        <div className={classes.titleWithDescription}>
          Card Conversion
          <span>Select a depot, from-to and the number of cards to be converted</span>
        </div>
      }
      onCancel={isLoading ? openCancelConfirmationModal : onCancel}
      closable={false}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <NGIForm name="convertCards" form={form} onFinish={openConfirmationModal} className={classes.formContainer}>
          <div className={classes.modalContent}>
            <Row className={classes.row}>
              <Col span={11}>
                <Title level={5}>Card State</Title>
                <Col span={24}>
                  <NGIFormItem rules={[{ required: true, message: "Please select sate" }]} name="state">
                    <NGISelect showSearch style={{ width: "100%" }} size="default" placeholder="Select state">
                      {["Available", "Retired"].map(item => (
                        <Option key={item} value={item}>
                          {item}
                        </Option>
                      ))}
                    </NGISelect>
                  </NGIFormItem>
                </Col>
              </Col>
              <Col span={12}>
                <Title level={5}>Convert To Type</Title>
                <Col span={24}>
                  <NGIFormItem rules={[{ required: true, message: "Please select type" }]} name="type">
                    <NGISelect showSearch style={{ width: "100%" }} size="default" placeholder="Select type">
                      {["RFID Card", "Virtual Card"].map(item => (
                        <Option key={item} value={item}>
                          {item}
                        </Option>
                      ))}
                    </NGISelect>
                  </NGIFormItem>
                </Col>
              </Col>
            </Row>
            <Row className={classes.row}>
              <Col span={11}>
                <Title level={5}>Depot</Title>
                <Col span={24}>
                  <NGIFormItem rules={[{ required: true, message: "Please select depot" }]} name="depot">
                    <NGISelect showSearch style={{ width: "100%" }} size="default" placeholder="Select depot">
                      {depot[USER_TENANT_NAME()]?.length > 0 &&
                        depot[USER_TENANT_NAME()]?.sort()?.map(
                          item => (
                            <Option key={item} value={item}>
                              {item}
                            </Option>
                          ),
                          this
                        )}
                    </NGISelect>
                  </NGIFormItem>
                </Col>
              </Col>
              <Col span={12}>
                <Title level={5}>Amount Converted</Title>
                <NGIFormItem
                  name="count"
                  rules={[
                    { required: true, message: "Please enter card count" },
                    {
                      required: true,
                      validator: async (_, value) => {
                        if (parseInt(value) > 50000) {
                          return Promise.reject(new Error("Value must be less than or equal to 50000"));
                        } else if (parseInt(value) < 1) {
                          return Promise.reject(new Error("Number of cards should be greater than 0."));
                        }
                      },
                    },
                  ]}
                >
                  <NGIInput type={"number"} min={1} placeholder="Enter No. of cards" />
                </NGIFormItem>
              </Col>
            </Row>
          </div>
        </NGIForm>
        <Space>
          <CustomButton onClick={onCancel} type="primary" color="outlined" size="small" text="Cancel" />
          <CustomButton
            disabled={!!resultId}
            onClick={form.submit}
            htmlType="submit"
            type="primary"
            size="small"
            text={"CONFIRM"}
          />
        </Space>
      </Spin>
      {isConfirmationOpen && (
        <ConfirmationModal
          description={<span>Are you sure you want to convert {getCount()} cards?</span>}
          onCancel={closeConfirmationModal}
          onConfirm={handleSave}
        />
      )}
      {isCancelConfirmationOpen && (
        <ConfirmationModal
          hideCancel
          description={
            <span>
              The process has already started. <b>Closing the popup doesn’t stop the process!</b>
              <br />
              <br />
              We will send you an email when the process is completed!
            </span>
          }
          onCancel={closeCancelConfirmationModal}
          cancelBtnTitle="Close"
          confirmBtnTitle="Ok"
          onConfirm={onConfirmClosingDialog}
        />
      )}
    </Modal>
  );
};

export default CardConversionModal;
